import React, { Suspense, useEffect, useState, useRef, useCallback } from "react";
import { Routes, Route, useParams } from "react-router-dom";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import { Analytics } from "@vercel/analytics/react"
import * as THREE from "three";

import './App.css';
import About from './pages/About';
import Projects from './pages/Projects';
import Blog from './pages/Blog';
import BlogPost from './components/BlogPost';

// Suppress the extension warning
if (typeof window !== 'undefined') {
  const originalWarn = console.warn;
  console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string') {
      if (args[0].includes('KHR_materials_pbrSpecularGlossiness') || 
          args[0].includes('outputEncoding')) {
        return; // Suppress these warnings
      }
    }
    originalWarn.apply(console, args);
  };
}

// Preload the model
useGLTF.preload("/bagel/scene.gltf");

function Model({ onLoad, fadeOut }) {
  const { scene } = useGLTF("/bagel/scene.gltf");
  const targetOpacity = useRef(1);
  const currentOpacity = useRef(1);
  const materials = useRef([]);
  
  // Update target opacity when fadeOut changes
  useEffect(() => {
    targetOpacity.current = fadeOut ? 0 : 1;
    // Also immediately update current opacity if materials are ready
    if (materials.current.length > 0) {
      materials.current.forEach((mat) => {
        if (mat) {
          mat.transparent = true;
        }
      });
    }
  }, [fadeOut]);
  
  // Center the model and fix materials
  useEffect(() => {
    if (scene) {
      // First, check if textures were already loaded by the GLTF loader
      let existingDiffuseTexture = null;
      let existingNormalTexture = null;
      
      scene.traverse((child) => {
        if (child.isMesh && child.material) {
          const mat = child.material;
          if (mat.map) existingDiffuseTexture = mat.map;
          if (mat.normalMap) existingNormalTexture = mat.normalMap;
        }
      });
      
      const textureLoader = new THREE.TextureLoader();
      
      // Load textures (use existing if available, otherwise load manually)
      const loadTextures = () => {
        return new Promise((resolve) => {
          let diffuseTexture = existingDiffuseTexture;
          let normalTexture = existingNormalTexture;
          let loadedCount = 0;
          const totalToLoad = (diffuseTexture ? 0 : 1) + (normalTexture ? 0 : 1);
          
          if (totalToLoad === 0) {
            resolve({ diffuseTexture, normalTexture });
            return;
          }
          
          const checkComplete = () => {
            loadedCount++;
            if (loadedCount === totalToLoad) {
              resolve({ diffuseTexture, normalTexture });
            }
          };
          
          if (!diffuseTexture) {
            diffuseTexture = textureLoader.load(
              "/bagel/textures/default_diffuse.png",
              (texture) => {
                texture.flipY = false;
                texture.colorSpace = THREE.SRGBColorSpace;
                checkComplete();
              },
              undefined,
              (error) => {
                console.error("Error loading diffuse texture:", error);
                checkComplete();
              }
            );
          }
          
          if (!normalTexture) {
            normalTexture = textureLoader.load(
              "/bagel/textures/default_normal.png",
              (texture) => {
                texture.flipY = false;
                checkComplete();
              },
              undefined,
              (error) => {
                console.error("Error loading normal texture:", error);
                checkComplete();
              }
            );
          }
        });
      };
      
      loadTextures().then(({ diffuseTexture, normalTexture }) => {
        // Clear materials array
        materials.current = [];
        
        // Fix materials - convert from deprecated KHR_materials_pbrSpecularGlossiness to standard PBR
        scene.traverse((child) => {
          if (child.isMesh && child.material) {
            const originalMaterial = child.material;
            
            // Create a new standard PBR material
            const newMaterial = new THREE.MeshStandardMaterial();
            
            // Copy basic properties
            newMaterial.side = originalMaterial.side || THREE.FrontSide;
            newMaterial.transparent = true; // Enable transparency for fading
            newMaterial.opacity = currentOpacity.current; // Use current opacity
            
            // Apply textures with proper settings
            if (diffuseTexture) {
              newMaterial.map = diffuseTexture;
              if (!existingDiffuseTexture) {
                // Only configure if we just loaded it
                diffuseTexture.flipY = false;
                diffuseTexture.colorSpace = THREE.SRGBColorSpace;
              }
            }
            if (normalTexture) {
              newMaterial.normalMap = normalTexture;
              if (!existingNormalTexture) {
                normalTexture.flipY = false;
              }
            }
            
            // Set PBR properties (convert from specular-glossiness approximation)
            // Glossiness 0.166 -> roughness ~0.8
            newMaterial.roughness = 0.8;
            newMaterial.metalness = 0.1;
            
            // Copy color if available
            if (originalMaterial.color) {
              newMaterial.color.copy(originalMaterial.color);
            } else {
              newMaterial.color.setRGB(1, 1, 1);
            }
            
            // Apply the new material
            child.material = newMaterial;
            
            // Store material reference for opacity animation
            materials.current.push(newMaterial);
          }
        });
        
        // Center the model (do this regardless of texture loading)
        const box = new THREE.Box3().setFromObject(scene);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        
        // Center the model
        scene.position.x = -center.x;
        scene.position.y = -center.y;
        scene.position.z = -center.z;
        
        // Scale if needed (optional - adjust based on model size)
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 0) {
          const scale = 1 / maxDim;
          scene.scale.multiplyScalar(scale);
        }
        
        // Rotate 45 degrees around X-axis so the hole faces the viewer
        scene.rotation.x = Math.PI / 2;
        
        // Notify that everything is loaded
        if (onLoad) {
          onLoad();
        }
      });
    }
  }, [scene, onLoad]);
  
  // Smoothly animate opacity
  useFrame(() => {
    if (materials.current.length === 0) return; // Wait for materials to be loaded
    
    const diff = targetOpacity.current - currentOpacity.current;
    if (Math.abs(diff) > 0.001) {
      currentOpacity.current += diff * 0.1; // Smooth interpolation
      materials.current.forEach((mat) => {
        if (mat) {
          mat.opacity = currentOpacity.current;
        }
      });
    } else if (materials.current.length > 0) {
      // Ensure opacity is set even when not animating
      materials.current.forEach((mat) => {
        if (mat) {
          mat.opacity = currentOpacity.current;
        }
      });
    }
  });
  
  return <primitive object={scene} />;
}

function RendererSetup() {
  const { gl } = useThree();
  useEffect(() => {
    // Ensure we're using the new API
    if (gl.outputColorSpace !== undefined) {
      gl.outputColorSpace = THREE.SRGBColorSpace;
    }
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = 1;
  }, [gl]);
  return null;
}

function CameraController({ onCameraChange }) {
  const { camera } = useThree();
  
  useFrame(() => {
    // Calculate distance from origin
    const distance = camera.position.length();
    // Call callback with distance
    if (onCameraChange) {
      onCameraChange(distance);
    }
  });
  
  return null;
}

function LoadingScreen() {
  return (
    <>
      <div className="loading-spinner"></div>
      <div className="loading-text">TOASTING...</div>
    </>
  );
}

function ScrollPrompt({ show }) {
  return (
    <div className={`scroll-prompt ${show ? 'visible' : 'hidden'}`}>
      <div className="scroll-prompt-text">Enter the bagel</div>
      <div className="scroll-arrow">↑</div>
    </div>
  );
}

function LinksMenu({ show }) {
  return (
    <div className={`links-menu ${show ? 'visible' : 'hidden'}`}>
      <nav className="links-nav">
        <a href="/about" className="link-item">ABOUT</a>
        <a href="/projects" className="link-item">PROJECTS</a>
        <a href="/blog" className="link-item">BLOG</a>
      </nav>
    </div>
  );
}

function BlogPostWrapper() {
  const { slug } = useParams();
  return <BlogPost slug={slug} />;
}

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptReady, setPromptReady] = useState(false);
  const currentDistance = useRef(3);
  
  const handleModelLoad = useCallback(() => {
    setIsLoaded(true);
    // Delay showing prompt for 5 seconds after model loads
    setTimeout(() => {
      setPromptReady(true);
      // Check if we should show prompt based on current distance
      if (currentDistance.current > 1) {
        setShowPrompt(true);
      }
    }, 5000);
  }, []);
  
  const handleCameraChange = useCallback((distance) => {
    currentDistance.current = distance;
    // Show links when camera is close enough (zoomed in)
    // Camera starts at distance ~3, show links when closer than 0.1
    const shouldShowLinks = distance < 0.05;
    setShowLinks(prev => {
      if (prev !== shouldShowLinks) return shouldShowLinks;
      return prev;
    });
    
    // Show/hide prompt based on distance (only if prompt is ready after delay)
    if (promptReady) {
      const shouldShowPrompt = distance > 1;
      setShowPrompt(prev => {
        if (prev !== shouldShowPrompt) return shouldShowPrompt;
        return prev;
      });
    }
  }, [promptReady]);
  
  return (
    <>
      <Routes>
        <Route path="/" element={
          <div className="App">
            <div className="App-header">
              <div className={`loading-screen ${isLoaded ? 'fade-out' : ''}`}>
                <LoadingScreen />
              </div>
              <Canvas 
                style={{ 
                  width: '100%', 
                  height: '100vh', 
                  opacity: isLoaded ? 1 : 0, 
                  transition: 'opacity 0.5s',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  zIndex: 1
                }} 
                camera={{ position: [0, 0, 3], fov: 50 }}
                gl={{ antialias: true }}
              >
                <color attach="background" args={['#000000']} />
                <RendererSetup />
                <CameraController onCameraChange={handleCameraChange} />
                <ambientLight intensity={0.5} />
                <directionalLight position={[10, 10, 5]} intensity={1} />
                <Suspense fallback={null}>
                  <Model onLoad={handleModelLoad} fadeOut={showLinks} />
                </Suspense>
                <OrbitControls 
                  enablePan={false}
                  enableZoom={true}
                  enableRotate={!showLinks}
                  minDistance={0.001}
                  maxDistance={1000}
                  autoRotate={false}
                />
              </Canvas>
              <ScrollPrompt show={showPrompt && isLoaded} />
              <LinksMenu show={showLinks} />
            </div>
          </div>
        } />
        <Route path="/about" element={<About />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPostWrapper />} />
      </Routes>
      <Analytics />
    </>
  );
}

export default App;
